import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography
} from '@mui/material';
import { Button } from 'system/atoms';
import { Dropdown, ServerDropdown } from 'common/form';
import { FormOption } from 'common/form/types/Form';
import { AlertCard } from 'common/ui/AlertCard';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { ToastType, useToast } from 'services/toast';
import { AtsJobLink } from 'services/integration/components/AtsJobLink';
import { useCreateAtsApplicationForCandidateMutation } from 'generated-graphql/hooks/apollo-hooks';
import { AtsApplicationCreateOutcome } from 'generated-graphql/types';
import { useAtsJobAlreadyAssociatedCandidateIds } from 'modules/ats/hooks/useAtsJobAlreadyAssociatedCandidateIds';
import { useAtsJobSearch } from 'modules/ats/hooks/useAtsJobSearch';
import { useCandidateListStore } from 'modules/candidate-profile/stores/useCandidateListStore';
import { addToJobModalSx } from 'modules/ats/styles';

export type AddToJobCandidate = {
  candidateProfileId: string;
  name: string;
  atsCandidateId?: string | null;
};

export type AddToJobDrawerContentProps = {
  organizationId: string;
  candidates: AddToJobCandidate[];
  // When opened from an analysis tied to an ATS job, preselect that job.
  initialAtsJobId?: string | null;
};

// The DropdownList popper hardcodes an inline z-index (1300) that sits below
// DrawerV2's (1400), so it would be clipped by the drawer. Raise it above the
// drawer with `!important` (needed to beat the component's inline style).
const DRAWER_POPPER_SX = { zIndex: '1401 !important' };

const pluraliseCandidates = (count: number) =>
  `${count} candidate${count === 1 ? '' : 's'}`;

const buildSubmitSummaryMessage = ({
  addedCount,
  skippedCount,
  failedCount
}: {
  addedCount: number;
  skippedCount: number;
  failedCount: number;
}) => {
  const parts = [
    addedCount > 0 ? `${pluraliseCandidates(addedCount)} added` : null,
    skippedCount > 0 ? `${pluraliseCandidates(skippedCount)} skipped` : null,
    failedCount > 0 ? `${pluraliseCandidates(failedCount)} failed` : null
  ].filter((part): part is string => part !== null);

  return parts.join(', ');
};

export const AddToJobDrawerContent: React.FC<AddToJobDrawerContentProps> = ({
  organizationId,
  candidates,
  initialAtsJobId
}) => {
  const { showToast } = useToast();
  const { closeDrawer, setPreventClose } = useDrawerV2();
  const atsName = useCandidateListStore((state) => state.integrationProvider);
  const [atsJobId, setAtsJobId] = useState<string | null>(null);
  const [initialJobOption, setInitialJobOption] = useState<FormOption | null>(
    null
  );
  const [isLoadingInitialJob, setIsLoadingInitialJob] = useState(
    !!initialAtsJobId
  );
  const [stageId, setStageId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [showJobError, setShowJobError] = useState(false);
  const [showStageError, setShowStageError] = useState(false);

  const {
    searchJobs,
    fetchJobById,
    getJob,
    error: jobsError
  } = useAtsJobSearch({ organizationId });

  // Preselect the analysis's ATS job: load it by id (so its label and stages
  // are cached), then select it. `atsJobId` is set only after the fetch so the
  // status dropdown and reconciliation resolve immediately from the cache.
  useEffect(() => {
    if (!initialAtsJobId) {
      return;
    }
    let isActive = true;
    const loadPreselectedJob = async () => {
      setIsLoadingInitialJob(true);
      const option = await fetchJobById(initialAtsJobId);
      if (!isActive) {
        return;
      }
      if (option) {
        setInitialJobOption(option);
        setAtsJobId(initialAtsJobId);
      }
      setIsLoadingInitialJob(false);
    };
    loadPreselectedJob();
    return () => {
      isActive = false;
    };
  }, [initialAtsJobId, fetchJobById]);

  // Stages ride along on the jobs query, so the selected job's statuses/labels
  // are resolved from the already-fetched job — no separate ATS round-trip.
  const stageOptions = useMemo(
    () => (atsJobId ? getJob(atsJobId)?.stageOptions ?? [] : []),
    [atsJobId, getJob]
  );

  // A chosen job with no selectable status/label (none configured in the ATS)
  // can't satisfy the required field, so block submit and explain why instead
  // of leaving the user stuck on an unusable dropdown.
  const isStageUnavailable = !!atsJobId && stageOptions.length === 0;

  const {
    associatedCandidateProfileIds,
    isReconciling,
    isReconcileComplete,
    reconcileError
  } = useAtsJobAlreadyAssociatedCandidateIds({
    atsJobId,
    candidates
  });

  const alreadyAssociatedCandidates = candidates.filter((candidate) =>
    associatedCandidateProfileIds.includes(candidate.candidateProfileId)
  );
  const eligibleCandidates = candidates.filter(
    (candidate) =>
      !associatedCandidateProfileIds.includes(candidate.candidateProfileId)
  );

  const [createAtsApplicationForCandidate] =
    useCreateAtsApplicationForCandidateMutation();

  const handleClose = () => {
    if (isSubmitting) return;
    closeDrawer();
  };

  const handleSubmit = async () => {
    if (!atsJobId) {
      setShowJobError(true);
      return;
    }
    if (!stageId) {
      setShowStageError(true);
      return;
    }
    if (eligibleCandidates.length === 0) return;

    const selectedJobLabel = getJob(atsJobId)?.label ?? '';

    setSubmittedCount(0);
    setIsSubmitting(true);
    // Block backdrop/Escape dismissal so the per-candidate requests aren't
    // abandoned mid-way (which could lead the user to retry and duplicate).
    setPreventClose(true);

    const outcomes: AtsApplicationCreateOutcome[] = [];
    // Intentionally sequential (not Promise.all) to stay within ATS rate limits.
    for (const candidate of eligibleCandidates) {
      try {
        const { data } = await createAtsApplicationForCandidate({
          variables: {
            input: {
              organizationId,
              candidateProfileId: candidate.candidateProfileId,
              atsJobId,
              ...(stageId ? { stageId } : {})
            }
          }
        });
        outcomes.push(
          data?.createAtsApplicationForCandidate?.outcome ??
            AtsApplicationCreateOutcome.Failed
        );
      } catch {
        outcomes.push(AtsApplicationCreateOutcome.Failed);
      }
      setSubmittedCount((count) => count + 1);
    }

    setIsSubmitting(false);
    setPreventClose(false);

    const { addedCount, skippedCount, failedCount } = outcomes.reduce(
      (counts, outcome) => {
        if (outcome === AtsApplicationCreateOutcome.Added)
          counts.addedCount += 1;
        else if (outcome === AtsApplicationCreateOutcome.Skipped)
          counts.skippedCount += 1;
        else counts.failedCount += 1;
        return counts;
      },
      { addedCount: 0, skippedCount: 0, failedCount: 0 }
    );
    const hasIssues = skippedCount > 0 || failedCount > 0;

    if (hasIssues) {
      showToast({
        message: buildSubmitSummaryMessage({
          addedCount,
          skippedCount,
          failedCount
        }),
        type: failedCount > 0 ? ToastType.Error : ToastType.Warning
      });
    } else {
      showToast({
        title: 'Candidates added',
        message: `To the ${selectedJobLabel} job`,
        type: ToastType.Success
      });
    }

    closeDrawer();
  };

  return (
    <Stack gap="20px" sx={{ position: 'relative' }}>
      <Stack direction="row" gap="12px" alignItems="flex-end">
        <Box sx={addToJobModalSx.jobDropdownWrapper}>
          <ServerDropdown
            name="add-to-job-select-job"
            field={{
              label: `Choose job from ${atsName}`,
              searchPlaceholder: 'Search by title or ID',
              minSearchLength: 3,
              // Fill most of the viewport height, leaving a gap at the bottom
              // rather than being capped to a short list.
              listMaxHeight: 'calc(100vh - 220px)',
              buttonText: 'Choose',
              popperSx: DRAWER_POPPER_SX,
              onSearchFilter: searchJobs,
              // With a preselected job we only fetch that job (by id); the full
              // list loads lazily when the user opens the picker to change it,
              // so there's no redundant unfiltered fetch on open.
              fetchOnMount: !initialAtsJobId,
              isLoading: isLoadingInitialJob,
              initialSelectedOption: initialJobOption ?? undefined,
              // Let the user clear a (pre)selected job and fall back to the
              // normal, unfiltered search.
              isClearable: !!atsJobId,
              clearLabel: 'job'
            }}
            value={atsJobId ?? ''}
            error={showJobError}
            errorMessage={showJobError ? 'Choose a job.' : undefined}
            onChange={(value) => {
              const nextJobId =
                (Array.isArray(value) ? value[0] : value) ?? null;
              setAtsJobId(nextJobId);
              setStageId(null);
              setShowJobError(false);
            }}
          />
        </Box>
        <AtsJobLink atsJobId={atsJobId} label="View job in ATS" />
      </Stack>

      {jobsError && (
        <AlertCard
          type="error"
          title="Couldn't load jobs from your ATS. Please close and try again."
        />
      )}

      {atsJobId && isReconciling && (
        <AlertCard
          type="info"
          isLoading
          title="Reconciling candidates, job and statuses"
        />
      )}

      {atsJobId && !isReconciling && reconcileError && (
        <AlertCard
          type="error"
          title="Couldn't confirm which candidates are already associated with this job. Please try again."
        />
      )}

      {atsJobId &&
        !isReconciling &&
        !reconcileError &&
        alreadyAssociatedCandidates.length > 0 && (
          <AlertCard
            type="info"
            title={`${pluraliseCandidates(
              alreadyAssociatedCandidates.length
            )} ${
              alreadyAssociatedCandidates.length === 1 ? 'is' : 'are'
            } already associated with this job.`}
          >
            <Stack gap="4px">
              <Box component="span">These people will not be added:</Box>
              <Box component="span" sx={addToJobModalSx.associatedList}>
                {alreadyAssociatedCandidates.map((candidate) => (
                  <Box
                    key={candidate.candidateProfileId}
                    component="span"
                    sx={addToJobModalSx.associatedListItem}
                  >
                    {candidate.name}
                  </Box>
                ))}
              </Box>
            </Stack>
          </AlertCard>
        )}

      {isStageUnavailable && (
        <AlertCard
          type="warning"
          title="This job has no statuses or labels to apply, so candidates can't be added to it."
        />
      )}

      {atsJobId && (
        <Dropdown
          name="add-to-job-select-stage"
          field={{
            label: 'Apply a status or label',
            subLabel: `To identify these candidates in ${atsName}.`,
            options: stageOptions,
            isSearchable: true,
            searchPlaceholder: 'Search statuses',
            buttonText: 'Choose',
            popperSx: DRAWER_POPPER_SX
          }}
          value={stageId ?? ''}
          error={showStageError}
          errorMessage={
            showStageError ? 'Choose a status or label.' : undefined
          }
          onChange={(value) => {
            setStageId((value as string) || null);
            setShowStageError(false);
          }}
        />
      )}

      <Stack direction="row" gap="16px">
        <Button
          data-testid="add-to-job-submit"
          variant="primary"
          disabled={
            isSubmitting ||
            isReconciling ||
            isStageUnavailable ||
            (!!atsJobId && !isReconcileComplete)
          }
          loading={isSubmitting}
          loadingType="bar"
          onClick={handleSubmit}
        >
          Add to job
        </Button>
        <Button
          data-testid="add-to-job-cancel"
          variant="tertiary"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </Stack>

      {isSubmitting && (
        <Box
          sx={addToJobModalSx.submitOverlay}
          data-testid="add-to-job-progress"
        >
          <CircularProgress />
          <Typography variant="h6">Adding candidates to the job</Typography>
          <LinearProgress
            variant="determinate"
            value={
              eligibleCandidates.length
                ? (submittedCount / eligibleCandidates.length) * 100
                : 0
            }
            sx={addToJobModalSx.submitOverlayProgress}
          />
          <Typography variant="body2">
            {submittedCount} of {eligibleCandidates.length} added. Please keep
            this open until it finishes.
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
